/**
 * Fetches the rule-based planning insights (GET /assistant/insights) and
 * derives the view state shared by the assistant's opening screens — the
 * drawer's `AssistantHome` and the full-screen `AssistantFocusHome`.
 *
 * Insights are deterministic, free, and fully localized here (the API returns
 * rule/chip IDs + params; copy lives under `assistant.insights.*` /
 * `assistant.chips.*`). Degrades gracefully: on a failed fetch a generic chip
 * set renders and chat is unaffected.
 */

import { useEffect, useState } from 'react';
import { AssistantInsightsResponse, getAssistantInsights } from 'services/api/assistant';

const FALLBACK_CHIPS = ['whats_next', 'getting_started', 'etiquette'];

export function useAssistantInsights(eventId: string) {
  const [insights, setInsights] = useState<AssistantInsightsResponse | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getAssistantInsights(eventId)
      .then((data) => {
        if (!cancelled) setInsights(data);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const loading = !insights && !failed;
  const chips = insights?.chips?.length ? insights.chips : FALLBACK_CHIPS;
  // "on_track" renders as the greeting's good news, not an action item.
  const actionInsights = (insights?.insights ?? []).filter((i) => i.id !== 'on_track');
  const onTrack = (insights?.insights ?? []).some((i) => i.id === 'on_track');

  return { insights, loading, failed, chips, actionInsights, onTrack };
}
